import clsx from 'clsx';
import { useId } from 'react';
import './controls.css';
import { Icon } from '@clubmed/trident-icons';
import { useValue } from '../hooks/useValue';
import type { FormControlProps } from './FormControl';
import { useInternalStatus } from '../hooks/useInternalStatus';

export interface FilterProps<Value = string> extends FormControlProps<Value> {
  /**
   * Is it attached to a dropdown?
   */
  hasDropdown?: boolean;

  onChange?: (name: string, value: Value | null) => void;
}

export function Filter<Value = string>(props: FilterProps<Value>) {
  const internalId = useId();

  const {
    id = internalId,
    name = id,
    className,
    dataTestId,
    disabled = false,
    checked: initialChecked = false,
    value,
    tabIndex = 0,
    validationStatus = 'default',
    errorMessage,
    onChange,
    hasDropdown,
    children,
    ...rest
  } = props;

  const { value: checked, setValue } = useValue<boolean>({
    name,
    initialValue: initialChecked,
    onChange(name, checked) {
      onChange?.(name, checked ? ((value !== undefined ? value : checked) as Value) : null);
    },
  });

  const internalStatus = useInternalStatus({
    isDisabled: disabled,
    validationStatus,
  });

  const shouldDisplayErrorMessage = internalStatus === 'error' && errorMessage;

  return (
    <div className={clsx(className, 'relative')}>
      <label data-testid={`filter-container-${id}`} className="relative">
        <input
          {...rest}
          name={name}
          data-testid={dataTestId}
          type="checkbox"
          tabIndex={tabIndex}
          onChange={() => {
            if (!disabled) setValue(!checked);
          }}
          checked={checked}
          data-name="Filter"
          disabled={disabled}
          value={value as any}
        />
        <span>
          <span className="font-semibold">{children}</span>

          <Icon className="check" width={null} name="CheckDefault" color="black" />

          {hasDropdown && (
            <Icon name="ArrowDefaultDown" className="ml-8" width="24px" color="black" />
          )}
        </span>
      </label>

      {shouldDisplayErrorMessage && (
        <span className="text-red text-b4 flex items-start space-x-4 ps-20" role="alert">
          <Icon name="Error" width="20px" />
          {errorMessage}
        </span>
      )}
    </div>
  );
}
